import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { PasswordResetRequestSchema, type PasswordResetRequest } from '@truecare/schemas';
import { Alert, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { ApiError } from '@truecare/api-client';
import { api } from '../api';
import { PortalShell } from '../components/PortalShell';

/**
 * Password-reset REQUEST page (`/password-reset/request`). The "Forgot password?"
 * link on the sign-in page points here. Previously this route did not exist —
 * the link 404'd, so there was no way to initiate a reset (the confirm page at
 * `/password-reset?token=` existed but could only be reached from the email).
 *
 * This is also how a passwordless (magic-link-only) account SETS a first
 * password: the `/me` surface only CHANGES an existing password now.
 *
 * Anti-enumeration: the backend always returns 200, so the success copy is
 * conditional-free ("if an account exists…").
 */
export const Route = createFileRoute('/password-reset_/request')({
  component: PasswordResetRequestPage,
});

function PasswordResetRequestPage() {
  const [sent, setSent] = useState(false);
  const form = useForm<PasswordResetRequest>({
    resolver: zodResolver(PasswordResetRequestSchema),
    defaultValues: { email: '' },
  });

  const mutation = useMutation({
    mutationFn: async (input: PasswordResetRequest) => {
      const { error } = await api.POST('/api/v1/auth/password-reset/request', {
        body: input,
      });
      if (error) throw new ApiError('Password-reset request failed', 0, error);
    },
    onSuccess: () => setSent(true),
  });

  return (
    <PortalShell signedOutSurface>
      <div className="mx-auto max-w-md px-6 pt-12 md:pt-20 pb-16">
        <Card padding="lg">
          <CardHeader
            title="Set or reset your password"
            description="Enter your account email and we'll send you a link to choose a new password. You can always sign in with a one-time email link instead."
          />
          {sent ? (
            <Alert tone="success" title="Check your email">
              If an account exists for that address, a password-reset link has
              been sent. It expires shortly — open it on this device, then choose
              a new password.
            </Alert>
          ) : (
            <form
              onSubmit={form.handleSubmit((data) => {
                mutation.mutate(data);
              })}
              className="flex flex-col gap-4"
              noValidate
            >
              {mutation.isError && (
                <Alert tone="danger">Something went wrong. Please try again.</Alert>
              )}
              <Field label="Email" error={form.formState.errors.email?.message}>
                <Input
                  type="email"
                  autoComplete="email"
                  autoFocus
                  {...form.register('email')}
                />
              </Field>
              <Button type="submit" loading={mutation.isPending}>
                Email me a reset link
              </Button>
            </form>
          )}
          <div className="mt-4 text-sm">
            <Link
              to="/sign-in"
              className="text-forest hover:text-gold underline-offset-2 hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        </Card>
      </div>
    </PortalShell>
  );
}
